import { useState, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { type components as AdminComponents } from "@njbsoft/types/admin";
import { ApiError } from "@njbsoft/api-client";
import { adminApiClient } from "@/lib/auth";
import { adminKeys } from "@/hooks/use-admin-keys";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";

type AdminParameter = AdminComponents["schemas"]["AdminParameter"];

const PAGE_SIZE = 20;

export const Route = createFileRoute("/_admin/parameters/orgs/$orgId")({
  component: OrgParametersPage,
});

const colHelper = createColumnHelper<AdminParameter>();

function OrgParametersPage() {
  const { orgId } = Route.useParams();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data, isLoading } = useQuery({
    queryKey: adminKeys.orgParameters(orgId, {
      search,
      category,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    queryFn: async () => {
      const {
        data: resp,
        error,
        response,
      } = await adminApiClient.GET("/orgs/{orgId}/parameters", {
        params: {
          path: { orgId },
          query: {
            search: search || undefined,
            category: category || undefined,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
          },
        },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to fetch org parameters";
        throw new ApiError(response.status, msg);
      }
      return resp;
    },
    staleTime: 60_000,
  });

  const columns = useMemo(
    () => [
      colHelper.accessor("code", {
        header: "Code",
        enableSorting: false,
        cell: (info) => <span className="font-mono text-sm">{info.getValue()}</span>,
      }),
      colHelper.accessor("name", {
        header: "Name",
        enableSorting: false,
      }),
      colHelper.accessor("category", {
        header: "Category",
        enableSorting: false,
        cell: (info) => (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
            {info.getValue()}
          </span>
        ),
      }),
      colHelper.accessor("unit", {
        header: "Unit",
        enableSorting: false,
        cell: (info) => <span className="text-sm text-slate-500">{info.getValue() ?? "-"}</span>,
      }),
      colHelper.accessor("cas_number", {
        header: "CAS Number",
        enableSorting: false,
        cell: (info) => <span className="text-sm text-slate-500">{info.getValue() ?? "-"}</span>,
      }),
      colHelper.accessor("source", {
        header: "Source",
        enableSorting: false,
        cell: (info) => {
          const val = info.getValue();
          return val === "custom" ? (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              Custom
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              System
            </span>
          );
        },
      }),
      colHelper.accessor("is_active", {
        header: "Active",
        enableSorting: false,
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
              Yes
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              No
            </span>
          ),
      }),
    ],
    [],
  );

  const categories = useMemo(() => {
    const items = data?.data ?? [];
    const unique = new Set(items.map((p) => p.category));
    return Array.from(unique).sort();
  }, [data]);

  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/parameters" className="text-sm text-blue-600 hover:underline">
          &larr; Back to Parameters
        </Link>
      </div>

      <h1 className="text-2xl font-bold text-slate-900">Organization Parameters</h1>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search parameters..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-64 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400"
        />
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
        >
          <option value="">All Categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        sorting={sorting}
        onSortingChange={setSorting}
        isLoading={isLoading}
        emptyMessage="No custom parameters found for this organization"
      />
      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
